// 唯一的请求出口（文档 21 §4.3 保险一）。
// 只读期（VITE_READONLY=1）拦截业务写请求；仅会话写入与本机推送退订例外。其余文件不许自己 fetch。

// 默认只读；登录和退出仅改变当前会话，不写业务数据。
import { useNetwork } from '@/stores/network';

const READONLY = import.meta.env.VITE_READONLY !== '0';
const SESSION_WRITES = new Set(['/api/auth/login', '/api/auth/logout']);

export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, query?: Query) {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    qs.set(k, v === true ? '1' : String(v));
  }
  const s = qs.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

/** 会话失效时通知壳层（显示重新登录引导，不丢当前页面） */
export const sessionEvents = new EventTarget();

function retryDelay(ms: number, signal?: AbortSignal | null) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('请求已取消', 'AbortError'));
    const timer = window.setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
    const abort = () => { window.clearTimeout(timer); reject(new DOMException('请求已取消', 'AbortError')); };
    signal?.addEventListener('abort', abort, { once: true });
  });
}

async function fetchWithNetworkRetry(path: string, init: RequestInit, method: string) {
  if (method !== 'GET' && !navigator.onLine) {
    useNetwork.getState().setOffline();
    window.dispatchEvent(new Event('ims:v2:write-offline'));
    throw new ApiError('网络已断开，操作没有提交', 0);
  }
  const waits = method === 'GET' ? [0, 250, 750] : [0];
  for (let attempt = 0; attempt < waits.length; attempt += 1) {
    if (waits[attempt]) await retryDelay(waits[attempt], init.signal);
    try {
      const response = await fetch(path, init);
      useNetwork.getState().setOnline();
      return response;
    } catch (cause) {
      if ((cause as Error).name === 'AbortError') throw cause;
      if (attempt + 1 === waits.length) {
        useNetwork.getState().setOffline();
        if (method !== 'GET') window.dispatchEvent(new Event('ims:v2:write-offline'));
        throw new ApiError(method === 'GET' ? '网络已断开，查询失败；已保留上次显示的内容' : '网络已断开，操作没有提交', 0);
      }
    }
  }
  throw new ApiError('网络请求失败', 0);
}

function waitForSessionRestore(signal?: AbortSignal | null) {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      sessionEvents.removeEventListener('restored', restored);
      signal?.removeEventListener('abort', aborted);
    };
    const restored = () => { cleanup(); resolve(); };
    const aborted = () => { cleanup(); reject(new DOMException('请求已取消', 'AbortError')); };
    if (signal?.aborted) return aborted();
    sessionEvents.addEventListener('restored', restored, { once: true });
    signal?.addEventListener('abort', aborted, { once: true });
  });
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || 'GET').toUpperCase();
  const pushRemoval = method === 'DELETE' && (path.startsWith('/api/push/subscriptions?endpoint=') || path.startsWith('/api/v2/push/subscriptions?endpoint='));
  if (READONLY && method !== 'GET' && !(method === 'POST' && SESSION_WRITES.has(path)) && !pushRemoval) {
    throw new ApiError(`只读模式：拒绝 ${method} ${path}`, 405);
  }
  const res = await fetchWithNetworkRetry(path, {
    ...init,
    method,
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init.headers || {}) },
  }, method);
  let body: { data?: T; error?: string; details?: unknown } | null = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (res.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    // 只让读请求在原地等重登并重发；写请求绝不自动重发，避免重复业务操作。
    if (method === 'GET' && !path.startsWith('/api/auth/')) {
      await waitForSessionRestore(init.signal);
      return request<T>(path, init);
    }
  }
  if (!res.ok) {
    throw new ApiError(body?.error || `请求失败（${res.status}）`, res.status, body?.details);
  }
  return body?.data as T;
}

/** 只读检查 V2 静态入口的资源版本；不访问业务 API。 */
export async function getV2IndexHtml() {
  const url = new URL(`${import.meta.env.BASE_URL}index.html`, window.location.origin);
  url.searchParams.set('check', String(Date.now()));
  const response = await fetchWithNetworkRetry(url.toString(), {
    method: 'GET', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'text/html' },
  }, 'GET');
  if (!response.ok) throw new ApiError(`检查更新失败（${response.status}）`, response.status);
  return response.text();
}

/** 归档 PDF 只允许同源 /uploads/ 路径，供预览、下载和分享统一使用。 */
export function archivedPdfUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin || url.search || url.hash || !/^\/uploads\/[^/]+\.pdf$/i.test(url.pathname)) return null;
    const filename = decodeURIComponent(url.pathname.slice('/uploads/'.length));
    if (!filename || filename.includes('/') || filename.includes('\\') || filename === '.' || filename === '..') return null;
    return url.toString();
  } catch { return null; }
}

/** 未锁账对账单只读现算预览，不进入共享归档。 */
export function statementPreviewPdfUrl(id: string, variant: 'summary' | 'detail'): string {
  return new URL(`/api/v2/settlements/statements/${encodeURIComponent(id)}/preview-pdf?variant=${variant}`, window.location.origin).toString();
}

export async function getStatementPreviewPdf(id: string, variant: 'summary' | 'detail'): Promise<Uint8Array<ArrayBuffer>> {
  const response = await fetchWithNetworkRetry(statementPreviewPdfUrl(id, variant), {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }, cache: 'no-store',
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后重试导出', 401);
  }
  if (!response.ok) throw new ApiError(`预览版 PDF 生成失败（${response.status}）`, response.status);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength > 25 * 1024 * 1024) throw new ApiError('单份 PDF 超过 25 MB，请单独下载', 413);
  if (bytes.byteLength < 5 || String.fromCharCode(...bytes.subarray(0, 5)) !== '%PDF-') throw new ApiError('预览文件不是有效的 PDF', 422);
  return bytes;
}

/** Read a locked statement PDF through its V2 statement visibility check. */
export function statementArchivedPdfUrl(id: string, variant: 'summary' | 'detail'): string {
  return new URL(`/api/v2/settlements/statements/${encodeURIComponent(id)}/pdf-file?variant=${variant}`, window.location.origin).toString();
}

export async function getStatementArchivedPdf(id: string, variant: 'summary' | 'detail'): Promise<Uint8Array<ArrayBuffer>> {
  const response = await fetchWithNetworkRetry(statementArchivedPdfUrl(id, variant), {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }, cache: 'no-store',
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后重试导出', 401);
  }
  if (!response.ok) throw new ApiError(`PDF 下载失败（${response.status}）`, response.status);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength > 25 * 1024 * 1024) throw new ApiError('单份 PDF 超过 25 MB，请单独下载', 413);
  if (bytes.byteLength < 5 || String.fromCharCode(...bytes.subarray(0, 5)) !== '%PDF-') throw new ApiError('归档文件不是有效的 PDF', 422);
  return bytes;
}

/** 读取老版已归档的同源 PDF。 */
export async function getArchivedPdf(path: string, signal?: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  const url = archivedPdfUrl(path);
  if (!url) throw new ApiError('PDF 地址不属于本系统的归档目录', 400);
  const response = await fetchWithNetworkRetry(url, {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }, signal,
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后重试导出', 401);
  }
  if (!response.ok) throw new ApiError(`PDF 下载失败（${response.status}）`, response.status);
  const declaredSize = Number(response.headers.get('content-length') || 0);
  if (declaredSize > 25 * 1024 * 1024) throw new ApiError('单份 PDF 超过 25 MB，请单独下载', 413);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength > 25 * 1024 * 1024) throw new ApiError('单份 PDF 超过 25 MB，请单独下载', 413);
  if (bytes.byteLength < 5 || String.fromCharCode(...bytes.subarray(0, 5)) !== '%PDF-') throw new ApiError('归档文件不是有效的 PDF', 422);
  return bytes;
}

/** 读取当前发货批次的同源 PDF，用于系统分享。 */
export async function getShipmentPdf(shipmentId: string, signal?: AbortSignal): Promise<Blob> {
  const path = `/api/v2/shipments/${encodeURIComponent(shipmentId)}/pdf`;
  const response = await fetchWithNetworkRetry(path, {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }, signal,
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后再分享', 401);
  }
  if (!response.ok) throw new ApiError(`发货单 PDF 生成失败（${response.status}）`, response.status);
  const blob = await response.blob();
  if (blob.size > 25 * 1024 * 1024) throw new ApiError('发货单 PDF 超过 25 MB', 413);
  return blob;
}

/** 服务器按当前共享订单及库存重新核对后生成只读拣货 PDF。 */
export async function getPickListPdf(orderIds: string[], options: { showProductImages?: boolean; showAmounts?: boolean } = {}, signal?: AbortSignal): Promise<{ blob: Blob; pageCount: number }> {
  if (!orderIds.length || orderIds.length > 50) throw new ApiError('请选择 1–50 张订单', 400);
  const query = new URLSearchParams({ ids: orderIds.join(','), images: options.showProductImages === false ? '0' : '1', amounts: options.showAmounts ? '1' : '0' });
  const path = `/api/v2/shipments/pick-list/pdf?${query}`;
  const response = await fetchWithNetworkRetry(path, {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }, signal,
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后再生成拣货单', 401);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new ApiError(body?.error || `拣货单 PDF 生成失败（${response.status}）`, response.status);
  }
  const blob = await response.blob();
  if (blob.size > 25 * 1024 * 1024) throw new ApiError('拣货单 PDF 超过 25 MB', 413);
  if (blob.type !== 'application/pdf' || (await blob.slice(0, 5).text()) !== '%PDF-') throw new ApiError('拣货单不是有效的 PDF', 422);
  const pageCount = Number(response.headers.get('X-Page-Count'));
  if (!Number.isInteger(pageCount) || pageCount < 1) throw new ApiError('拣货单页数读取失败，请重新生成', 422);
  return { blob, pageCount };
}

/** 画册分享只读取老版同源上传图片，避免把任意 URL 带入文件分享。 */
export async function getGalleryImage(path: string): Promise<Blob> {
  const url = new URL(path, window.location.origin);
  if (url.origin !== window.location.origin || url.search || url.hash || !/^\/uploads\/[^/]+$/.test(url.pathname)) {
    throw new ApiError('图片不属于本系统的上传目录', 400);
  }
  const response = await fetchWithNetworkRetry(url.toString(), {
    method: 'GET', credentials: 'include', headers: { Accept: 'image/*' },
  }, 'GET');
  if (response.status === 401) {
    sessionEvents.dispatchEvent(new Event('expired'));
    throw new ApiError('登录已过期，请重新登录后再分享', 401);
  }
  if (!response.ok) throw new ApiError(`图片读取失败（${response.status}）`, response.status);
  const declaredSize = Number(response.headers.get('content-length') || 0);
  if (declaredSize > 20 * 1024 * 1024) throw new ApiError('单张图片超过 20 MB', 413);
  const blob = await response.blob();
  if (!blob.type.startsWith('image/') || blob.size > 20 * 1024 * 1024) throw new ApiError('上传文件不是受支持的图片', 422);
  return blob;
}

export const api = {
  get<T>(path: string, query?: Query, signal?: AbortSignal) {
    return request<T>(withQuery(path, query), { signal });
  },
  login(username: string, password: string) {
    return request<{ id: string; username: string; name: string }>('/api/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }),
    });
  },
  logout() {
    return request<{ success: boolean }>('/api/auth/logout', { method: 'POST' });
  },
  removePushSubscription(endpoint: string) {
    return request<{ success: boolean }>(withQuery('/api/push/subscriptions', { endpoint }), { method: 'DELETE' });
  },
  post<T>(path: string, body: unknown) {
    return request<T>(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  },
  postForm<T>(path: string, body: FormData) {
    return request<T>(path, { method: 'POST', body });
  },
  patch<T>(path: string, body: unknown) {
    return request<T>(path, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  },
  delete<T>(path: string) {
    return request<T>(path, { method: 'DELETE' });
  },
};

export function setupPassword(payload: { token: string; password?: string; inspect?: boolean }) {
  return api.post<{ username: string; name: string; passwordMinLength: number }>('/api/v2/auth/password-setup', payload);
}
